import { colorSystem } from '@luminovo/design-system';
import { styled } from '@mui/material';

export const ROW_HEIGHT = 32;
export const BAR_HEIGHT = 12;

// One grid for all rows, so labels, bars and values line up like the table's columns.
export const Rows = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'minmax(96px, max-content) minmax(120px, 1fr) max-content',
    alignContent: 'start',
});

const rowStyle = {
    gridColumn: '1 / -1',
    display: 'grid',
    gridTemplateColumns: 'subgrid',
    alignItems: 'center',
    columnGap: '16px',
    height: ROW_HEIGHT,
    padding: '0 16px',
    borderBottom: `1px solid ${colorSystem.neutral[2]}`,
    // The card's border closes the list, as in the table.
    '&:last-child': { borderBottom: 'none' },
    '&:hover, &:focus-visible': { background: colorSystem.neutral[1] },
    '&:focus-visible': { outline: `2px solid ${colorSystem.primary[5]}`, outlineOffset: '-2px' },
} as const;

export const Row = styled('div')(rowStyle);

export const RowButton = styled('button')({
    // Before the spread, so the row's hairline survives the button reset.
    border: 'none',
    ...rowStyle,
    width: '100%',
    background: 'none',
    font: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
});

export const Track = styled('span')({ position: 'relative', height: ROW_HEIGHT, display: 'block' });

export const Segment = styled('span')({
    position: 'absolute',
    top: (ROW_HEIGHT - BAR_HEIGHT) / 2,
    height: BAR_HEIGHT,
    borderRadius: 2,
});

export type BarExtent = { negative: number; positive: number };

/** Where zero sits and how wide a value is, as percentages of the bar column. */
export function barScale(extents: BarExtent[]): { zero: number; percent: (value: number) => number } {
    const min = Math.min(0, ...extents.map((e) => e.negative));
    const max = Math.max(0, ...extents.map((e) => e.positive));
    const span = max - min || 1;
    return { zero: (Math.abs(min) / span) * 100, percent: (value) => (Math.abs(value) / span) * 100 };
}

export const ZeroLine = styled('span')({
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 1,
    background: colorSystem.neutral[4],
});
